import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../apiWithAccessToken";
import "./TokenDisplay.css";

const DEMO_LIMIT_MODAL_KEY = "demo_limit_exhausted_seen";

const formatTokens = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return "Unlimited";
  }
  if (numeric >= 1000000) {
    return `${(numeric / 1000000).toFixed(1)}m`;
  }
  if (numeric >= 1000) {
    return `${(numeric / 1000).toFixed(1)}k`;
  }
  return `${numeric}`;
};

const getTokenState = (used, quota) => {
  if (!Number.isFinite(quota) || quota <= 0) {
    return { key: "plenty", label: "Full" };
  }

  const usageRatio = used / quota;
  if (usageRatio < 0.6) return { key: "plenty", label: "Full" };
  if (usageRatio < 0.85) return { key: "limited", label: "Limited" };
  return { key: "low", label: "Low" };
};

const getDemoExhaustedKey = (runtime) =>
  `${DEMO_LIMIT_MODAL_KEY}:${runtime?.tenant_id || "tenant"}`;

const applyTokenPayload = (previous, data = {}) => ({
  used: Number(data.used ?? data.user_used ?? previous.used ?? 0),
  quota: Number(data.quota ?? data.user_quota ?? previous.quota ?? 0),
  remaining: data.remaining ?? data.user_remaining ?? previous.remaining ?? 0,
  tenant_used: Number(data.tenant_used ?? previous.tenant_used ?? 0),
  tenant_quota:
    data.tenant_quota === null || data.tenant_quota === undefined
      ? previous.tenant_quota ?? null
      : Number(data.tenant_quota),
  tenant_remaining:
    data.tenant_remaining === null || data.tenant_remaining === undefined
      ? previous.tenant_remaining ?? null
      : Number(data.tenant_remaining),
  tenant_demo_cap_applies: Boolean(
    data.tenant_demo_cap_applies ?? previous.tenant_demo_cap_applies
  ),
});

export default function TokenDisplay({ userRole = "", runtime = null, quotaPolicy = null }) {
  const [tokens, setTokens] = useState({
    used: 0,
    quota: 0,
    remaining: 0,
    tenant_used: 0,
    tenant_quota: null,
    tenant_remaining: null,
    tenant_demo_cap_applies: false,
  });
  const [showDemoLimitModal, setShowDemoLimitModal] = useState(false);
  const isMounted = useRef(true);

  const fetchTokens = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return;
    }

    try {
      const { data } = await api.get("/token_usage");
      if (isMounted.current) {
        setTokens((previous) => applyTokenPayload(previous, data));
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        return;
      }
      console.error(error);
      if (isMounted.current) {
        toast.error("Failed to load token usage");
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchTokens();
    const interval = setInterval(fetchTokens, 5000);

    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [fetchTokens]);

  useEffect(() => {
    const handleQuotaUpdate = (event) => {
      if (!isMounted.current) return;
      setTokens((previous) => applyTokenPayload(previous, event.detail || {}));
    };

    window.addEventListener("geneilytics:quota-updated", handleQuotaUpdate);
    return () => {
      window.removeEventListener("geneilytics:quota-updated", handleQuotaUpdate);
    };
  }, []);

  const isDemoMode = runtime?.billing_mode === "demo";
  const isTenantAdmin = String(userRole || "").toLowerCase() === "admin";
  const shouldShowTenantUsage =
    isDemoMode &&
    isTenantAdmin &&
    Number.isFinite(tokens.tenant_quota) &&
    tokens.tenant_quota > 0;

  const tokenState = useMemo(
    () => getTokenState(tokens.used, tokens.quota),
    [tokens.quota, tokens.used]
  );

  useEffect(() => {
    if (!isDemoMode || !tokens.tenant_demo_cap_applies) {
      return;
    }

    const isExhausted =
      Number.isFinite(tokens.tenant_remaining) && Number(tokens.tenant_remaining) <= 0;
    if (!isExhausted) {
      return;
    }

    const storageKey = getDemoExhaustedKey(runtime);
    if (sessionStorage.getItem(storageKey) === "1") {
      return;
    }

    sessionStorage.setItem(storageKey, "1");
    setShowDemoLimitModal(true);
  }, [
    isDemoMode,
    runtime,
    tokens.tenant_demo_cap_applies,
    tokens.tenant_remaining,
  ]);

  const handleCloseDemoModal = () => {
    setShowDemoLimitModal(false);
  };

  return (
    <>
      <div className="token-pills" aria-label="Usage status">
        {isDemoMode ? <span className="token-demo-badge">Demo</span> : null}

        {shouldShowTenantUsage ? (
          <div className="token-total-pill" aria-label="Total tenant tokens used">
            Total used {formatTokens(tokens.tenant_used)} / {formatTokens(tokens.tenant_quota)}
          </div>
        ) : null}

        <div className={`token-status ${tokenState.key}`} aria-label="User token usage status">
          <span className="token-dot" />
          <span className="token-text">{tokenState.label}</span>
          <div className="token-tooltip">
            Used {formatTokens(tokens.used)} of {formatTokens(tokens.quota)} tokens
          </div>
        </div>
      </div>

      {showDemoLimitModal ? (
        <div className="demo-limit-modal-backdrop" role="presentation">
          <div
            className="demo-limit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-limit-title"
          >
            <h3 id="demo-limit-title">Demo token limit exhausted</h3>
            <p>
              This demo tenant has exhausted its token limit. Further AI usage will remain blocked
              until the demo quota is increased or the tenant is moved out of demo mode.
            </p>
            {quotaPolicy?.tenant_demo_token_limit ? (
              <div className="demo-limit-meta">
                Total demo usage: {formatTokens(tokens.tenant_used)} /{" "}
                {formatTokens(quotaPolicy.tenant_demo_token_limit)}
              </div>
            ) : null}
            <div className="demo-limit-actions">
              <button type="button" onClick={handleCloseDemoModal}>
                Okay
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
